import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Loader2,
  AlertCircle,
  RefreshCw,
  Building2,
  User,
  Clock,
  FileText,
  Crown,
  Sparkles,
  TrendingUp,
  CheckCircle,
  Send,
  Copy,
  Wand2,
  Moon,
  Sun,
  ExternalLink,
  Ticket,
  Info,
  Lightbulb,
  BookOpen,
  Search,
  ArrowUpCircle,
  Timer,
  Paperclip,
  Shield,
  AlertTriangle,
  Download,
  Image,
  Bot,
  Phone,
  Mail,
  X,
  Play,
  PenLine,
} from 'lucide-react'
import { AlertsTab } from '@/components/AlertsTab'
import { DeviceContextCard } from '@/components/DeviceContextCard'
import { RecordingPanel } from '@/components/RecordingPanel'
import { useLiteContext } from '@/hooks/useLiteContext'
import { useAssetContext } from '@/hooks/useAssetContext'
import { useSimilarTickets } from '@/hooks/useSimilarTickets'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { formatDate, getPriorityColor, getStatusColor } from '@/lib/utils'
import {
  analyzeTicket,
  sendChatMessage,
  addNote,
  addTimeEntry,
  generateResponse,
  fetchStatuses,
  updateTicket,
  fetchSLA,
  fetchAttachments,
  fetchEntitlements,
  createSalesTicket,
  createDocumentationTicket,
} from '@/services/api'
import { Textarea } from '@/components/ui/textarea'
import type { AnalysisResult, ChatMessage, ChatImageAttachment, SLAInfo, Attachment, EntitlementResponse } from '@/types/halopsa'

// Metric configuration (matching snpod)
type MetricKey = 'clarity' | 'sentiment' | 'empathy' | 'acumen' | 'cadence' | 'disruption' | 'age' | 'priority' | 'chronic' | 'skills' | 'vip' | 'responseQuality'

interface MetricConfig {
  key: MetricKey
  label: string
  invertColor?: boolean
}

const METRICS: MetricConfig[] = [
  { key: 'clarity', label: 'CLARITY' },
  { key: 'sentiment', label: 'SENTIMENT' },
  { key: 'empathy', label: 'EMPATHY' },
  { key: 'acumen', label: 'ACUMEN', invertColor: true },
  { key: 'cadence', label: 'CADENCE' },
  { key: 'disruption', label: 'DISRUPTION', invertColor: true },
  { key: 'age', label: 'AGE', invertColor: true },
  { key: 'priority', label: 'PRIORITY', invertColor: true },
  { key: 'chronic', label: 'CHRONIC', invertColor: true },
  { key: 'skills', label: 'SKILLS', invertColor: true },
  { key: 'vip', label: 'VIP' },
  { key: 'responseQuality', label: 'RESPONSE' },
]

// Rotating thinking messages for AI loading states
const THINKING_MESSAGES = [
  'Analyzing the details...',
  'Searching backend database...',
  'Hang on a moment more...',
  'Parsing the data...',
  'Consulting the knowledge base...',
  'This will be worth the wait...',
  'Reviewing ticket history...',
  'Almost there...',
  'Processing your request...',
  'Gathering insights...',
]

function getScoreColor(score: number, invert: boolean = false, isDark: boolean = false): string {
  const effectiveScore = invert ? 11 - score : score
  if (isDark) {
    if (effectiveScore >= 7) return 'bg-gradient-to-br from-emerald-800/80 to-emerald-900/90 border-emerald-400/60'
    if (effectiveScore >= 4) return 'bg-gradient-to-br from-amber-800/80 to-amber-900/90 border-amber-400/60'
    return 'bg-gradient-to-br from-red-800/80 to-red-900/90 border-red-400/60'
  }
  if (effectiveScore >= 7) return 'bg-emerald-50 border-emerald-300'
  if (effectiveScore >= 4) return 'bg-amber-50 border-amber-300'
  return 'bg-red-50 border-red-300'
}

function getScoreTextColor(score: number, invert: boolean = false, isDark: boolean = false): string {
  const effectiveScore = invert ? 11 - score : score
  if (isDark) {
    if (effectiveScore >= 7) return 'text-emerald-300'
    if (effectiveScore >= 4) return 'text-amber-300'
    return 'text-red-300'
  }
  if (effectiveScore >= 7) return 'text-emerald-600'
  if (effectiveScore >= 4) return 'text-amber-600'
  return 'text-red-600'
}

// Image attachment limits for Ask Bot (must stay in sync with api/lite/chat.ts).
const MAX_IMAGES = 4
const MAX_IMAGE_DIMENSION = 1568 // longest edge; Anthropic's recommended max before downscaling
const MAX_ATTACHMENT_BASE64 = 1_200_000 // ~1.2MB of base64 per image before falling back to JPEG
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']

// Ask Bot history is persisted per-ticket in localStorage so it survives tab
// switches and ticket reloads. Storage is browser-local only.
const chatStorageKey = (ticketId: number | string) => `rezolve:askai:${ticketId}`

function loadStoredMessages(ticketId: number | string | undefined): ChatMessage[] {
  if (!ticketId) return []
  try {
    const raw = localStorage.getItem(chatStorageKey(ticketId))
    const parsed = raw ? JSON.parse(raw) : null
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveStoredMessages(ticketId: number | string | undefined, messages: ChatMessage[]) {
  if (!ticketId) return
  const key = chatStorageKey(ticketId)
  try {
    localStorage.setItem(key, JSON.stringify(messages))
  } catch {
    // Quota likely exceeded by image data — retry with text-only history so the
    // conversation still survives, even if thumbnails don't.
    try {
      const textOnly = messages.map(m => ({ role: m.role, content: m.content }))
      localStorage.setItem(key, JSON.stringify(textOnly))
    } catch {
      /* give up — history just won't persist this round */
    }
  }
}

const attachmentToDataUrl = (img: ChatImageAttachment) => `data:${img.mediaType};base64,${img.data}`

function readFileAsDataURL(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

function loadImageEl(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new window.Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

// Convert a pasted/selected image into a base64 attachment, downscaling large
// images so the request payload stays small and within the serverless body limit.
async function fileToAttachment(file: File | Blob): Promise<ChatImageAttachment | null> {
  const type = file.type
  if (!ALLOWED_IMAGE_TYPES.includes(type)) return null
  const dataUrl = await readFileAsDataURL(file)
  const base64 = (url: string) => url.split(',')[1] || ''

  // Don't rasterize GIFs — that would drop animation. Send as-is.
  if (type === 'image/gif') {
    return { mediaType: type, data: base64(dataUrl) }
  }

  try {
    const img = await loadImageEl(dataUrl)
    const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(img.width, img.height))
    const width = Math.max(1, Math.round(img.width * scale))
    const height = Math.max(1, Math.round(img.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return { mediaType: type, data: base64(dataUrl) }
    ctx.drawImage(img, 0, 0, width, height)

    // Keep the source format when reasonable, but fall back to JPEG if a lossless
    // PNG/WebP encode is large, so the upload stays within the request body limit.
    let outType = type === 'image/png' || type === 'image/webp' ? type : 'image/jpeg'
    let data = base64(canvas.toDataURL(outType, 0.85))
    if (outType !== 'image/jpeg' && data.length > MAX_ATTACHMENT_BASE64) {
      outType = 'image/jpeg'
      data = base64(canvas.toDataURL('image/jpeg', 0.85))
    }
    return { mediaType: outType, data }
  } catch {
    return { mediaType: type, data: base64(dataUrl) }
  }
}

export function LitePanel() {
  // Read URL parameters for initial state
  const urlParams = new URLSearchParams(window.location.search)
  const initialTab = urlParams.get('tab') || 'summary'
  const initialTheme = urlParams.get('theme')

  const [activeTab, setActiveTab] = useState(initialTab)
  const { context, loading, error, refetch } = useLiteContext()

  // Ticket id drives per-ticket chat persistence (see chatMessages below).
  const ticketIdForChat = context?.ticket.id

  // Similar tickets state
  const {
    tickets: similarTickets,
    loading: similarLoading,
    findSimilar,
  } = useSimilarTickets()

  // Asset context state
  const {
    assetContext,
    loading: assetsLoading,
    fetchAssets,
  } = useAssetContext()

  // Analysis state
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null)
  const [analyzingTicket, setAnalyzingTicket] = useState(false)
  const [analysisError, setAnalysisError] = useState<string | null>(null)
  const [selectedMetric, setSelectedMetric] = useState<MetricKey | null>(null)

  // Chat state. History is persisted per-ticket in localStorage (loaded/saved by
  // the effects below) so it survives reloads and tab switches.
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>(() => loadStoredMessages(ticketIdForChat))
  const [chatInput, setChatInput] = useState('')
  const [chatImages, setChatImages] = useState<ChatImageAttachment[]>([])
  const [processingImage, setProcessingImage] = useState(false)
  const [sendingChat, setSendingChat] = useState(false)
  const chatEndRef = useRef<HTMLDivElement>(null)
  const chatFileInputRef = useRef<HTMLInputElement>(null)

  // Actions state
  const [noteText, setNoteText] = useState('')
  const [noteType, setNoteType] = useState<'internal' | 'customer'>('internal')
  const [addingNote, setAddingNote] = useState(false)
  const [timeMinutesTotal, setTimeMinutesTotal] = useState(15)
  const [timeNote, setTimeNote] = useState('')
  const [timeBillable, setTimeBillable] = useState(true)
  const [addingTime, setAddingTime] = useState(false)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)

  // Documentation request state
  const [showDocModal, setShowDocModal] = useState(false)
  const [docSummary, setDocSummary] = useState('')
  const [generatingDocSummary, setGeneratingDocSummary] = useState(false)
  const [creatingDocTicket, setCreatingDocTicket] = useState(false)
  const [docResult, setDocResult] = useState<{ ticketId: number; docUrl?: string; docTitle?: string; todoSections?: string[] } | null>(null)

  // Generate Response state
  const [responseTone, setResponseTone] = useState<'professional' | 'friendly' | 'technical'>('professional')
  const [generatedResponse, setGeneratedResponse] = useState('')
  const [generatingResponse, setGeneratingResponse] = useState(false)

  // Status update state
  const [statuses, setStatuses] = useState<Array<{ id: number; name: string }>>([])
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [showStatusDropdown, setShowStatusDropdown] = useState(false)

  // SLA state
  const [slas, setSlas] = useState<SLAInfo[]>([])
  const [slaLoading, setSlaLoading] = useState(false)

  // Attachments state
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [attachmentsLoading, setAttachmentsLoading] = useState(false)

  // Entitlements state
  const [entitlements, setEntitlements] = useState<EntitlementResponse | null>(null)
  const [entitlementsLoading, setEntitlementsLoading] = useState(false)

  // Dark mode state
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      if (initialTheme === 'dark') return true
      if (initialTheme === 'light') return false
      return document.documentElement.classList.contains('dark') ||
        window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return false
  })


  // Rotating loading message state
  const [thinkingMessageIndex, setThinkingMessageIndex] = useState(0)

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [isDark])


  // Rotate thinking messages when loading
  useEffect(() => {
    const isLoading = sendingChat || analyzingTicket
    if (!isLoading) {
      setThinkingMessageIndex(0)
      return
    }

    const interval = setInterval(() => {
      setThinkingMessageIndex(prev => (prev + 1) % THINKING_MESSAGES.length)
    }, 2500) // Rotate every 2.5 seconds

    return () => clearInterval(interval)
  }, [sendingChat, analyzingTicket])

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages])

  // Load persisted chat history when the ticket becomes known (or changes). The
  // lazy initializer above covers the case where context is ready on first render;
  // this covers the common case where context loads asynchronously after mount.
  useEffect(() => {
    if (ticketIdForChat) {
      setChatMessages(loadStoredMessages(ticketIdForChat))
    }
  }, [ticketIdForChat])

  // Persist chat history whenever it changes (best-effort; see saveStoredMessages).
  useEffect(() => {
    if (ticketIdForChat) {
      saveStoredMessages(ticketIdForChat, chatMessages)
    }
  }, [ticketIdForChat, chatMessages])

  // Load SLA, attachments, entitlements, assets, and auto-start analysis when context loads
  useEffect(() => {
    if (context?.ticket.id) {
      loadSLAs()
      loadAttachments()
      fetchAssets(context.ticket.id, context.contact?.id, context.company?.id)
      if (context.company?.id) {
        loadEntitlements()
      }
      // Auto-start analysis in background so it's ready when user switches tabs
      if (!analysis && !analyzingTicket) {
        handleAnalyze()
      }
    }
  }, [context?.ticket.id])

  const loadSLAs = async () => {
    if (!context?.ticket.id) return
    setSlaLoading(true)
    try {
      const result = await fetchSLA(context.ticket.id)
      setSlas(result.slas || [])
    } catch (err) {
      console.error('Failed to load SLAs:', err)
    } finally {
      setSlaLoading(false)
    }
  }

  const loadAttachments = async () => {
    if (!context?.ticket.id) return
    setAttachmentsLoading(true)
    try {
      const result = await fetchAttachments(context.ticket.id)
      setAttachments(result.attachments || [])
    } catch (err) {
      console.error('Failed to load attachments:', err)
    } finally {
      setAttachmentsLoading(false)
    }
  }

  const loadEntitlements = async () => {
    if (!context?.company?.id) return
    setEntitlementsLoading(true)
    try {
      const result = await fetchEntitlements(context.company.id)
      setEntitlements(result)
    } catch (err) {
      console.error('Failed to load entitlements:', err)
    } finally {
      setEntitlementsLoading(false)
    }
  }

  // Handle analyze ticket
  const handleAnalyze = async () => {
    if (!context?.ticket.id) return
    setAnalyzingTicket(true)
    setAnalysisError(null)
    setSelectedMetric(null)
    try {
      const result = await analyzeTicket(context.ticket.id)
      setAnalysis(result)
    } catch (err: unknown) {
      setAnalysisError(err instanceof Error ? err.message : 'Analysis failed')
    } finally {
      setAnalyzingTicket(false)
    }
  }

  // Handle create sales opportunity ticket
  const handleCreateSalesTicket = useCallback(async (): Promise<{ ticketId: number } | void> => {
    if (!context?.ticket.id || !context?.company?.id || !analysis?.salesOpportunity) return
    const opp = analysis.salesOpportunity
    const result = await createSalesTicket({
      ticket_id: context.ticket.id,
      company_id: context.company.id,
      company_name: context.company.name || 'Unknown',
      opportunity_type: opp.type || 'General',
      summary: opp.summary || 'Sales opportunity detected by AI analysis',
      signals: opp.signals || [],
    })
    return { ticketId: result.ticket.id }
  }, [context, analysis])

  // Handle send chat message
  const handleSendChat = async () => {
    if (!context?.ticket.id) return
    const userMessage = chatInput.trim()
    const attached = chatImages
    if ((!userMessage && attached.length === 0) || sendingChat) return

    setChatInput('')
    setChatImages([])
    setSendingChat(true)

    // Snapshot the history sent to the API (text-only) before adding the new turn.
    const history = chatMessages
    const newUserMsg: ChatMessage = {
      role: 'user',
      content: userMessage,
      images: attached.length > 0 ? attached : undefined,
    }
    setChatMessages(prev => [...prev, newUserMsg])

    // When only images are attached, give the model a default instruction.
    const apiMessage = userMessage || 'Please take a look at the attached image(s) and help with this ticket.'

    try {
      const result = await sendChatMessage(context.ticket.id, apiMessage, history, attached)
      const responseText = result.response || 'I apologize, but I was unable to generate a response. Please try again.'
      const assistantMsg: ChatMessage = { role: 'assistant', content: responseText }
      setChatMessages(prev => [...prev, assistantMsg])
    } catch (err: unknown) {
      console.error('[Chat] Error:', err)
      const errorMsg: ChatMessage = { role: 'assistant', content: `Error: ${err instanceof Error ? err.message : 'Failed to get response. Please try again.'}` }
      setChatMessages(prev => [...prev, errorMsg])
    } finally {
      setSendingChat(false)
    }
  }

  // Add image files (from paste or file picker), downscaling and capping at MAX_IMAGES.
  const addChatFiles = async (files: FileList | File[]) => {
    const candidates = Array.from(files).filter(f => ALLOWED_IMAGE_TYPES.includes(f.type))
    if (candidates.length === 0) return
    setProcessingImage(true)
    try {
      const remaining = MAX_IMAGES - chatImages.length
      const processed: ChatImageAttachment[] = []
      for (const file of candidates.slice(0, Math.max(0, remaining))) {
        const att = await fileToAttachment(file)
        if (att?.data) processed.push(att)
      }
      if (processed.length > 0) {
        setChatImages(prev => [...prev, ...processed].slice(0, MAX_IMAGES))
      }
    } finally {
      setProcessingImage(false)
    }
  }

  const handleChatPaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return
    const files: File[] = []
    for (const item of Array.from(items)) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile()
        if (file) files.push(file)
      }
    }
    if (files.length > 0) {
      e.preventDefault() // don't paste the image as garbage text into the textarea
      void addChatFiles(files)
    }
  }

  const handleChatFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) void addChatFiles(e.target.files)
    e.target.value = '' // allow re-selecting the same file
  }

  const removeChatImage = (idx: number) => setChatImages(prev => prev.filter((_, i) => i !== idx))

  const handleChatKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter submits; Shift+Enter inserts a newline (multi-line prompts).
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (!sendingChat) void handleSendChat()
    }
  }

  // Handle add note
  const handleAddNote = async () => {
    if (!context?.ticket.id || !noteText.trim()) return
    setAddingNote(true)
    try {
      await addNote(context.ticket.id, noteText.trim(), noteType === 'internal' ? 'internal' : 'public')
      setNoteText('')
      setActionSuccess(`${noteType === 'internal' ? 'Internal' : 'Customer'} note added!`)
      setTimeout(() => setActionSuccess(null), 3000)
      refetch()
    } catch (err: unknown) {
      setActionSuccess(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`)
      setTimeout(() => setActionSuccess(null), 3000)
    } finally {
      setAddingNote(false)
    }
  }

  // Handle add time
  const handleAddTime = async () => {
    if (!context?.ticket.id || timeMinutesTotal === 0) return
    setAddingTime(true)
    try {
      const hours = Math.floor(timeMinutesTotal / 60)
      const minutes = timeMinutesTotal % 60
      await addTimeEntry(context.ticket.id, hours, minutes, timeNote || undefined, timeBillable)
      setTimeMinutesTotal(15)
      setTimeNote('')
      const timeStr = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
      setActionSuccess(`${timeStr} logged${timeBillable ? '' : ' (non-billable)'}!`)
      setTimeout(() => setActionSuccess(null), 3000)
    } catch (err: unknown) {
      setActionSuccess(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`)
      setTimeout(() => setActionSuccess(null), 3000)
    } finally {
      setAddingTime(false)
    }
  }

  // Handle escalation (coming soon)
  const handleEscalate = () => {
    setActionSuccess('Escalation feature coming soon!')
    setTimeout(() => setActionSuccess(null), 3000)
  }

  // Handle documentation request
  const handleDocumentation = async () => {
    setShowDocModal(true)
    setGeneratingDocSummary(true)
    setDocSummary('')

    try {
      // Build context from chat messages and ticket
      const chatContext = chatMessages.length > 0
        ? `\n\nUser Questions from Ask Bot:\n${chatMessages
            .filter(m => m.role === 'user')
            .map(m => `- ${m.content}`)
            .join('\n')}`
        : ''

      // Generate AI summary for documentation request
      const result = await sendChatMessage(
        context!.ticket.id,
        `Based on this support ticket and the user's questions, identify what documentation or knowledge base articles are missing or need to be created. Be specific about what topics should be documented. Format as a clear, actionable documentation request that can be assigned to the documentation team.

Ticket: ${context!.ticket.summary}
Details: ${context!.ticket.details}${chatContext}

Generate a concise documentation request (2-4 sentences) explaining what documentation is needed.`,
        []
      )
      setDocSummary(result.response || 'Documentation needed for this issue. Please review and specify requirements.')
    } catch (err) {
      console.error('[Documentation] Error generating summary:', err)
      setDocSummary('Documentation needed for this issue. Please review and add specific requirements.')
    } finally {
      setGeneratingDocSummary(false)
    }
  }

  // Create documentation ticket via real endpoint
  const handleCreateDocTicket = async () => {
    if (!context?.ticket.id || !docSummary.trim()) return
    setCreatingDocTicket(true)
    setDocResult(null)

    try {
      const result = await createDocumentationTicket({
        company_id: context.company?.id || 0,
        company_name: context.company?.name || 'Unknown',
        missing_doc_summary: docSummary,
        original_ticket_id: context.ticket.id,
        chat_history: chatMessages.map(m => ({ role: m.role, content: m.content })),
      })

      setDocResult({
        ticketId: result.ticket_id,
        docUrl: result.doc_url,
        docTitle: result.doc_title,
        todoSections: result.todo_sections,
      })

      let successMsg = `Documentation ticket #${result.ticket_id} created!`
      if (result.doc_url) {
        successMsg += ` ${result.doc_provider?.toUpperCase()} doc linked.`
      }
      setActionSuccess(successMsg)
      setTimeout(() => setActionSuccess(null), 5000)
    } catch (err) {
      console.error('[Documentation] Error creating ticket:', err)
      setActionSuccess(`Error: ${err instanceof Error ? err.message : 'Failed to create documentation request'}`)
      setTimeout(() => setActionSuccess(null), 5000)
    } finally {
      setCreatingDocTicket(false)
    }
  }

  // Handle generate response
  const handleGenerateResponse = async () => {
    if (!context?.ticket.id) return
    setGeneratingResponse(true)
    try {
      const result = await generateResponse(context.ticket.id, responseTone, 'concise')
      setGeneratedResponse(result.response)
    } catch (err: unknown) {
      setActionSuccess(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`)
      setTimeout(() => setActionSuccess(null), 3000)
    } finally {
      setGeneratingResponse(false)
    }
  }

  // Handle copy to clipboard
  const handleCopyResponse = () => {
    navigator.clipboard.writeText(generatedResponse)
    setActionSuccess('Copied to clipboard!')
    setTimeout(() => setActionSuccess(null), 2000)
  }

  // Handle use response as note
  const handleUseAsNote = () => {
    setNoteText(generatedResponse)
    setGeneratedResponse('')
    setActionSuccess('Response added to note field')
    setTimeout(() => setActionSuccess(null), 2000)
  }

  // Load statuses when dropdown is opened
  const handleLoadStatuses = async () => {
    if (statuses.length > 0) {
      setShowStatusDropdown(true)
      return
    }
    try {
      const result = await fetchStatuses()
      setStatuses(result.statuses)
      setShowStatusDropdown(true)
    } catch (err) {
      console.error('Failed to load statuses:', err)
    }
  }

  // Handle status update
  const handleUpdateStatus = async (statusId: number) => {
    if (!context?.ticket.id) return
    setUpdatingStatus(true)
    try {
      console.log('[Status] Updating ticket', context.ticket.id, 'to status', statusId)
      const result = await updateTicket(context.ticket.id, { status_id: statusId })
      console.log('[Status] Update result:', result)
      setShowStatusDropdown(false)
      setActionSuccess('Status updated successfully')
      setTimeout(() => setActionSuccess(null), 3000)
      refetch()
    } catch (err: any) {
      console.error('Failed to update status:', err)
      setActionSuccess(`Failed to update status: ${err.message || 'Unknown error'}`)
      setTimeout(() => setActionSuccess(null), 5000)
    } finally {
      setUpdatingStatus(false)
    }
  }

  // Suggested questions component - larger buttons with bold text
  const SuggestedQuestions = () => (
    <div className="grid grid-cols-3 gap-2">
      <button
        onClick={() => setChatInput('What is the quickest way to resolve this issue?')}
        className={`text-left text-xs rounded-lg px-3 py-2.5 border-2 transition-all flex items-center gap-2 ${
          isDark
            ? 'bg-slate-800 border-slate-600 hover:border-primary hover:bg-slate-700 text-slate-200'
            : 'bg-white border-gray-200 hover:border-primary hover:bg-primary/5 text-gray-700'
        }`}
      >
        <Lightbulb className="h-4 w-4 text-primary flex-shrink-0" />
        <span className="font-semibold">Quick fix?</span>
      </button>
      <button
        onClick={() => setChatInput('Find documentation or knowledge base articles for this issue')}
        className={`text-left text-xs rounded-lg px-3 py-2.5 border-2 transition-all flex items-center gap-2 ${
          isDark
            ? 'bg-slate-800 border-slate-600 hover:border-blue-500 hover:bg-slate-700 text-slate-200'
            : 'bg-white border-gray-200 hover:border-blue-500 hover:bg-blue-50 text-gray-700'
        }`}
      >
        <BookOpen className="h-4 w-4 text-blue-500 flex-shrink-0" />
        <span className="font-semibold">Find KB</span>
      </button>
      <button
        onClick={() => setChatInput('Find similar tickets and how they were resolved')}
        className={`text-left text-xs rounded-lg px-3 py-2.5 border-2 transition-all flex items-center gap-2 ${
          isDark
            ? 'bg-slate-800 border-slate-600 hover:border-purple-500 hover:bg-slate-700 text-slate-200'
            : 'bg-white border-gray-200 hover:border-purple-500 hover:bg-purple-50 text-gray-700'
        }`}
      >
        <Copy className="h-4 w-4 text-purple-500 flex-shrink-0" />
        <span className="font-semibold">Similar</span>
      </button>
      <button
        onClick={() => setChatInput('What are the possible root causes of this issue?')}
        className={`text-left text-xs rounded-lg px-3 py-2.5 border-2 transition-all flex items-center gap-2 ${
          isDark
            ? 'bg-slate-800 border-slate-600 hover:border-amber-500 hover:bg-slate-700 text-slate-200'
            : 'bg-white border-gray-200 hover:border-amber-500 hover:bg-amber-50 text-gray-700'
        }`}
      >
        <Search className="h-4 w-4 text-amber-500 flex-shrink-0" />
        <span className="font-semibold">Root cause?</span>
      </button>
      <button
        onClick={() => setChatInput('Summarize this ticket for me')}
        className={`text-left text-xs rounded-lg px-3 py-2.5 border-2 transition-all flex items-center gap-2 ${
          isDark
            ? 'bg-slate-800 border-slate-600 hover:border-cyan-500 hover:bg-slate-700 text-slate-200'
            : 'bg-white border-gray-200 hover:border-cyan-500 hover:bg-cyan-50 text-gray-700'
        }`}
      >
        <FileText className="h-4 w-4 text-cyan-500 flex-shrink-0" />
        <span className="font-semibold">Summarize</span>
      </button>
      <button
        onClick={() => setChatInput('Who should I escalate this issue to?')}
        className={`text-left text-xs rounded-lg px-3 py-2.5 border-2 transition-all flex items-center gap-2 ${
          isDark
            ? 'bg-slate-800 border-slate-600 hover:border-rose-500 hover:bg-slate-700 text-slate-200'
            : 'bg-white border-gray-200 hover:border-rose-500 hover:bg-rose-50 text-gray-700'
        }`}
      >
        <ArrowUpCircle className="h-4 w-4 text-rose-500 flex-shrink-0" />
        <span className="font-semibold">Escalate?</span>
      </button>
    </div>
  )

  // Loading state
  if (loading) {
    return (
      <div className={`flex items-center justify-center h-screen ${isDark ? 'bg-slate-900' : 'bg-background'}`}>
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>Loading ticket...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={`flex items-center justify-center h-screen p-4 ${isDark ? 'bg-slate-900' : 'bg-background'}`}>
        <div className="text-center max-w-sm">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-3" />
          <h2 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : ''}`}>Error Loading Ticket</h2>
          <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>{error}</p>
          <Button onClick={refetch} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  // No context
  if (!context) {
    return (
      <div className={`flex items-center justify-center h-screen p-4 ${isDark ? 'bg-slate-900' : 'bg-background'}`}>
        <div className="text-center">
          <FileText className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-muted-foreground'}`} />
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>No ticket data available</p>
        </div>
      </div>
    )
  }

  const { ticket, company, contact, notes } = context

  return (
    <div className={`h-screen flex flex-col p-3 ${isDark ? 'bg-slate-900' : 'bg-background'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-sm">H</span>
          </div>
          <div>
            <h1 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-foreground'}`}>HaloPod</h1>
            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>Ticket #{ticket.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            onClick={() => {
              const ticketId = context?.ticket.id
              if (ticketId) {
                const url = `${window.location.origin}${window.location.pathname}?view=secondscreen&ticketId=${ticketId}`
                window.open(url, 'halopod_secondscreen', 'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no')
              }
            }}
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title="Open Second Screen"
          >
            <ExternalLink className={`w-4 h-4 ${isDark ? 'text-slate-300' : ''}`} />
          </Button>
          <Button onClick={() => setIsDark(!isDark)} variant="ghost" size="icon" className="h-8 w-8">
            {isDark ? <Sun className="w-4 h-4 text-slate-300" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button onClick={refetch} variant="ghost" size="icon" className="h-8 w-8">
            <RefreshCw className={`w-4 h-4 ${isDark ? 'text-slate-300' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Tabs - 6 tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex-1 flex flex-col min-h-0">
        <TabsList className={`w-full grid grid-cols-6 mb-3 flex-shrink-0 ${isDark ? 'bg-slate-800' : ''}`}>
          <TabsTrigger value="summary" className={isDark ? 'data-[state=active]:bg-slate-700' : ''}>Summary</TabsTrigger>
          <TabsTrigger value="analysis" className={isDark ? 'data-[state=active]:bg-slate-700' : ''}>Analysis</TabsTrigger>
          <TabsTrigger value="alerts" className={`relative ${isDark ? 'data-[state=active]:bg-slate-700' : ''}`}>
            Alerts
            {analysis && (() => {
              const v = analysis.sentiment?.metrics?.vip
              const e = analysis.sentiment?.escalation
              const m = analysis.sentiment?.metrics?.multiUserEvent
              const c = analysis.sentiment?.metrics?.chronic
              const s = analysis.salesOpportunity
              const count = [
                v?.isVip || (v?.score && v.score >= 7) || contact?.isVip,
                e?.recommended || (e?.score && e.score >= 7),
                (m as any)?.isMultiUser || (m?.score && m.score >= 5),
                c?.score && c.score >= 7,
                s?.detected && (s?.confidence ?? 0) >= 60,
              ].filter(Boolean).length
              return count > 0 ? (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 text-[9px] font-bold text-white bg-red-500 rounded-full">
                  {count}
                </span>
              ) : null
            })()}
          </TabsTrigger>
          <TabsTrigger value="chat" className={isDark ? 'data-[state=active]:bg-slate-700' : ''}>Ask Bot</TabsTrigger>
          <TabsTrigger value="actions" className={isDark ? 'data-[state=active]:bg-slate-700' : ''}>Actions</TabsTrigger>
          <TabsTrigger value="recording" className={isDark ? 'data-[state=active]:bg-slate-700' : ''}>Recording</TabsTrigger>
        </TabsList>

        {/* Summary Tab */}
        <TabsContent value="summary" className="flex-1 overflow-y-auto space-y-3">
          {/* Ticket Info Card */}
          <div className={`rounded-lg border p-3 space-y-3 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
            <div>
              <h2 className={`font-medium ${isDark ? 'text-white' : 'text-foreground'}`}>{ticket.summary}</h2>
              <p className={`text-xs mt-1 line-clamp-2 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                {ticket.details}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 relative">
              {/* Status - Clickable */}
              <div className="relative">
                <button
                  onClick={handleLoadStatuses}
                  className={`inline-flex items-center h-6 px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all ${getStatusColor(ticket.status)}`}
                  disabled={updatingStatus}
                >
                  {updatingStatus ? <Loader2 className="w-3 h-3 animate-spin inline" /> : ticket.status?.replace(/\(\s*\)/, '').trim()}
                </button>
                {showStatusDropdown && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowStatusDropdown(false)} />
                    <div className={`absolute top-full left-0 mt-1 border rounded-md shadow-lg z-20 min-w-32 max-h-48 overflow-y-auto ${isDark ? 'bg-slate-800 border-slate-600' : 'bg-card'}`}>
                      {statuses.map(s => (
                        <button
                          key={s.id}
                          onClick={() => handleUpdateStatus(s.id)}
                          className={`w-full px-3 py-1.5 text-xs text-left transition-colors ${isDark ? 'hover:bg-slate-700 text-slate-200' : 'hover:bg-muted'}`}
                        >
                          {s.name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <span className={`inline-flex items-center h-6 px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                {ticket.priority?.replace(/\(\s*\)/, '').trim()}
              </span>
              {ticket.impactLevel != null && ticket.impactLevel > 0 && (
                <span className={`inline-flex items-center h-6 px-2 py-0.5 rounded-full text-xs font-medium ${isDark ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                  Impact {ticket.impactLevel}
                </span>
              )}
              {ticket.urgencyLevel != null && ticket.urgencyLevel > 0 && (
                <span className={`inline-flex items-center h-6 px-2 py-0.5 rounded-full text-xs font-medium ${isDark ? 'bg-cyan-900/50 text-cyan-300' : 'bg-cyan-100 text-cyan-700'}`}>
                  Urgency {ticket.urgencyLevel}
                </span>
              )}
              {ticket.ticketType && (
                <span className={`inline-flex items-center h-6 px-2 py-0.5 rounded-full text-xs font-medium ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600'}`}>
                  {ticket.ticketType}
                </span>
              )}
            </div>

            <div className={`text-xs flex items-center gap-1 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
              <Clock className="w-3 h-3" />
              Created {formatDate(ticket.dateEntered)}
            </div>
          </div>

          {/* Device Context */}
          <DeviceContextCard assetContext={assetContext} loading={assetsLoading} isDark={isDark} />

          {/* Recent Notes - Moved up */}
          {notes.length > 0 && (
            <div className={`rounded-lg border p-3 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
              <h3 className={`text-xs font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                Recent Notes ({notes.length})
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {notes.slice(0, 5).map((note) => (
                  <div
                    key={note.id}
                    className={`text-xs p-2 rounded ${
                      note.isPrivate
                        ? isDark ? 'bg-yellow-900/20 border-l-2 border-yellow-500' : 'bg-yellow-50 border-l-2 border-yellow-400'
                        : isDark ? 'bg-slate-700' : 'bg-muted'
                    }`}
                  >
                    <div className="flex justify-between mb-1">
                      <span className={`font-medium ${isDark ? 'text-slate-200' : ''}`}>{note.createdBy}</span>
                      <span className={isDark ? 'text-slate-500' : 'text-muted-foreground'}>
                        {formatDate(note.dateCreated)}
                      </span>
                    </div>
                    <p className={`line-clamp-2 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>{note.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Company & Contact - with phone/email */}
          <div className="grid grid-cols-2 gap-3">
            {company && (
              <div className={`rounded-lg border p-3 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`} />
                  <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>Company</span>
                </div>
                <p className={`text-sm font-medium ${isDark ? 'text-white' : ''}`}>{company.name}</p>
              </div>
            )}

            {contact && (
              <div className={`rounded-lg border p-3 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <User className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`} />
                  <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>Contact</span>
                  {contact.isVip && <Crown className="w-3 h-3 text-yellow-500" />}
                </div>
                <p className={`text-sm font-medium ${isDark ? 'text-white' : ''}`}>{contact.name}</p>
                {contact.jobTitle && (
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>{contact.jobTitle}</p>
                )}
                {contact.email && (
                  <div className={`flex items-center gap-1 mt-1 text-xs ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                    <Mail className="w-3 h-3" />
                    <a href={`mailto:${contact.email}`} className="hover:text-primary">{contact.email}</a>
                  </div>
                )}
                {contact.phone && (
                  <div className={`flex items-center gap-1 mt-0.5 text-xs ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                    <Phone className="w-3 h-3" />
                    <a href={`tel:${contact.phone}`} className="hover:text-primary">{contact.phone}</a>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* SLA Status */}
          <div className={`rounded-lg border p-3 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
            <h3 className={`font-medium text-xs flex items-center gap-1.5 mb-2 ${isDark ? 'text-slate-300' : 'text-muted-foreground'}`}>
              <Timer className="h-3 w-3" />
              SLA Status
            </h3>
            {slaLoading ? (
              <div className="flex items-center gap-2 py-2">
                <Loader2 className="h-3 w-3 animate-spin text-primary" />
                <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>Loading...</span>
              </div>
            ) : slas.length === 0 ? (
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-muted-foreground'}`}>No SLA information</p>
            ) : (
              <div className="space-y-1.5">
                {slas.slice(0, 3).map((sla) => (
                  <div
                    key={sla.id}
                    className={`text-xs rounded border p-1.5 ${
                      sla.hasBreached
                        ? isDark ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-300'
                        : sla.percentageUsed > 75
                        ? isDark ? 'bg-amber-900/30 border-amber-700' : 'bg-amber-50 border-amber-300'
                        : isDark ? 'bg-emerald-900/30 border-emerald-700' : 'bg-emerald-50 border-emerald-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`font-medium ${isDark ? 'text-slate-200' : ''}`}>{sla.name}</span>
                      {sla.hasBreached && <AlertTriangle className="h-3 w-3 text-red-500" />}
                    </div>
                    <div className="flex items-center justify-between text-[9px]">
                      <span className={sla.hasBreached ? 'text-red-500' : isDark ? 'text-slate-400' : 'text-gray-500'}>
                        {sla.hasBreached ? 'BREACHED' : sla.timeRemaining}
                      </span>
                      <span className={isDark ? 'text-slate-500' : 'text-gray-400'}>{Math.round(sla.percentageUsed)}%</span>
                    </div>
                    <div className={`w-full h-1 rounded-full mt-1 ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
                      <div
                        className={`h-full rounded-full ${
                          sla.hasBreached ? 'bg-red-500' : sla.percentageUsed > 75 ? 'bg-amber-500' : 'bg-emerald-500'
                        }`}
                        style={{ width: `${Math.min(sla.percentageUsed, 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Attachments */}
          <div className={`rounded-lg border p-3 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
            <h3 className={`font-medium text-xs flex items-center gap-1.5 mb-2 ${isDark ? 'text-slate-300' : 'text-muted-foreground'}`}>
              <Paperclip className="h-3 w-3" />
              Attachments
            </h3>
            {attachmentsLoading ? (
              <div className="flex items-center gap-2 py-2">
                <Loader2 className="h-3 w-3 animate-spin text-primary" />
                <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>Loading...</span>
              </div>
            ) : attachments.length === 0 ? (
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-muted-foreground'}`}>No attachments</p>
            ) : (
              <div className="space-y-1">
                {attachments.slice(0, 4).map((att) => (
                  <a
                    key={att.id}
                    href={att.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-1.5 text-xs p-1 rounded transition-colors group ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}`}
                  >
                    {att.contentType?.startsWith('image/') ? (
                      <Image className="h-3 w-3 text-purple-500 flex-shrink-0" />
                    ) : (
                      <FileText className="h-3 w-3 text-blue-500 flex-shrink-0" />
                    )}
                    <span className={`truncate flex-1 group-hover:text-primary ${isDark ? 'text-slate-300' : ''}`}>
                      {att.filename}
                    </span>
                    <span className={`text-[9px] flex-shrink-0 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                      {att.filesize}
                    </span>
                    <Download className={`h-2.5 w-2.5 group-hover:text-primary flex-shrink-0 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                  </a>
                ))}
                {attachments.length > 4 && (
                  <p className={`text-[9px] text-center pt-1 ${isDark ? 'text-slate-500' : 'text-muted-foreground'}`}>
                    +{attachments.length - 4} more
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Entitlements - Simplified */}
          {company && (
            <div className={`rounded-lg border p-3 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
              <h3 className={`font-medium text-xs flex items-center gap-1.5 mb-2 ${isDark ? 'text-slate-300' : 'text-muted-foreground'}`}>
                <Shield className="h-3 w-3 text-purple-500" />
                Customer Entitlements
              </h3>
              {entitlementsLoading ? (
                <div className="flex items-center gap-2 py-2">
                  <Loader2 className="h-3 w-3 animate-spin text-primary" />
                  <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>Loading...</span>
                </div>
              ) : !entitlements || entitlements.contracts.length === 0 ? (
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-muted-foreground'}`}>No active contracts</p>
              ) : (
                <div className="space-y-1.5">
                  {entitlements.contracts.slice(0, 3).map((contract) => (
                    <div
                      key={contract.id}
                      className={`text-xs rounded border p-2 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}
                    >
                      <div className={`font-medium ${isDark ? 'text-slate-200' : ''}`}>{contract.name}</div>
                      {contract.type && (
                        <div className={`text-[9px] ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{contract.type}</div>
                      )}
                      {contract.prepayHoursRemaining !== undefined && (
                        <div className={`text-[9px] mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          {contract.prepayHoursRemaining}h / {contract.prepayHours}h remaining
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Similar Tickets */}
          <div className={`rounded-lg border p-3 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
            <div className="mb-2">
              <h3 className={`text-xs font-medium flex items-center gap-1.5 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                <Ticket className="w-3 h-3" />
                Similar Tickets
              </h3>
            </div>
            {similarLoading ? (
              <div className="flex items-center gap-2 py-2">
                <Loader2 className="h-3 w-3 animate-spin text-primary" />
                <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>Finding...</span>
              </div>
            ) : similarTickets.length === 0 ? (
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-muted-foreground'}`}>
                Click{' '}
                <button
                  onClick={() => context?.ticket.id && findSimilar(context.ticket.id)}
                  className="text-primary font-medium hover:underline"
                >
                  Find
                </button>
                {' '}to discover related tickets
              </p>
            ) : (
              <div className="space-y-1.5">
                {similarTickets.slice(0, 5).map((t) => (
                  <a
                    key={t.id}
                    href={t.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block text-xs border-l-2 pl-1.5 hover:border-primary transition-colors ${isDark ? 'border-slate-600' : 'border-muted'}`}
                  >
                    <div className="flex items-center gap-1">
                      <span className={`font-medium ${isDark ? 'text-primary' : 'text-primary'}`}>#{t.id}</span>
                      <ExternalLink className={`h-2 w-2 ${isDark ? 'text-slate-500' : 'text-muted-foreground'}`} />
                    </div>
                    <p className={`line-clamp-1 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>{t.summary}</p>
                  </a>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Analysis Tab */}
        <TabsContent value="analysis" className="flex-1 overflow-y-auto space-y-3">
          {!analysis && !analyzingTicket && (
            <div className={`rounded-lg border p-4 text-center ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
              <Sparkles className="w-8 h-8 text-primary mx-auto mb-2" />
              <h3 className={`font-medium mb-1 ${isDark ? 'text-white' : ''}`}>AI Analysis</h3>
              <p className={`text-sm mb-3 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                Analyze this ticket with AI to get sentiment scores, escalation recommendations, and suggested actions.
              </p>
              {analysisError && <p className="text-sm text-destructive mb-3">{analysisError}</p>}
              <Button className="w-full" onClick={handleAnalyze}>
                <Sparkles className="w-4 h-4 mr-2" />
                Analyze Ticket
              </Button>
            </div>
          )}

          {analyzingTicket && (
            <div className={`rounded-lg border p-4 text-center ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
              <p className={`text-sm transition-opacity duration-300 ${isDark ? 'text-slate-300' : 'text-muted-foreground'}`}>{THINKING_MESSAGES[thinkingMessageIndex]}</p>
              <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-muted-foreground/60'}`}>AI analysis typically takes 15-20 seconds</p>
            </div>
          )}

          {analysis && !analyzingTicket && (
            <>
              {/* Summary */}
              <div className={`rounded-lg border p-3 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
                <h3 className={`text-xs font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>AI Summary</h3>
                <p className={`text-sm ${isDark ? 'text-slate-200' : ''}`}>{analysis.summary}</p>
              </div>

              {/* Suggested Actions */}
              {analysis.suggestedActions && analysis.suggestedActions.length > 0 && (
                <div className={`rounded-lg border p-3 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
                  <h3 className={`text-xs font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                    Suggested Actions
                  </h3>
                  <ul className="space-y-1">
                    {analysis.suggestedActions.map((action, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs">
                        <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className={isDark ? 'text-slate-200' : ''}>{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* 12-Metric Grid - 4 columns, centered, clickable */}
              {analysis.sentiment.metrics && (
                <div className={`rounded-lg border p-3 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
                  <h3 className={`text-xs font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                    Metric Scores
                  </h3>
                  <div className="grid grid-cols-4 gap-1">
                    {METRICS.map(({ key, label, invertColor }) => {
                      const metricData = analysis.sentiment.metrics[key]
                      const score = typeof metricData === 'object' && 'score' in metricData ? metricData.score : 0
                      const isSelected = selectedMetric === key
                      return (
                        <button
                          key={key}
                          onClick={() => setSelectedMetric(isSelected ? null : key)}
                          className={`rounded border p-1.5 text-center transition-all ${getScoreColor(score, invertColor, isDark)} ${isSelected ? 'ring-2 ring-primary' : ''}`}
                        >
                          <div className={`text-[9px] font-bold mb-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{label}</div>
                          <div className={`text-lg font-bold ${getScoreTextColor(score, invertColor, isDark)}`}>{score}</div>
                          <div className={`text-[8px] ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>/ 10</div>
                        </button>
                      )
                    })}
                  </div>

                  {/* Explanation Expansion */}
                  {selectedMetric && analysis.sentiment.metrics[selectedMetric] && (
                    <div className={`mt-2 p-2 rounded border animate-in fade-in ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <Info className="h-3 w-3 text-primary" />
                        <span className={`text-[10px] font-semibold ${isDark ? 'text-slate-200' : ''}`}>
                          {METRICS.find(m => m.key === selectedMetric)?.label} Explanation
                        </span>
                      </div>
                      <p className={`text-[10px] ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                        {typeof analysis.sentiment.metrics[selectedMetric] === 'object' && 'explanation' in analysis.sentiment.metrics[selectedMetric]
                          ? (analysis.sentiment.metrics[selectedMetric] as { explanation?: string }).explanation || 'No explanation available'
                          : 'No explanation available'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Skills Required */}
              {analysis.sentiment.metrics?.skills && (
                <div className={`rounded-lg border p-3 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
                  <h3 className={`text-xs font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                    Skills Required
                  </h3>
                  {typeof analysis.sentiment.metrics.skills === 'object' && 'required' in analysis.sentiment.metrics.skills &&
                   Array.isArray((analysis.sentiment.metrics.skills as { required?: string[] }).required) &&
                   (analysis.sentiment.metrics.skills as { required: string[] }).required.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {(analysis.sentiment.metrics.skills as { required: string[] }).required.slice(0, 6).map((skill, idx) => (
                        <span
                          key={idx}
                          className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium ${
                            isDark ? 'bg-blue-900/30 border border-blue-700 text-blue-300' : 'bg-blue-100 border border-blue-300 text-blue-700'
                          }`}
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-muted-foreground'}`}>No specific skills identified</p>
                  )}
                </div>
              )}

              {/* Escalation */}
              {analysis.sentiment.escalation && (
                <div className={`rounded-lg border p-3 ${
                  analysis.sentiment.escalation.recommended
                    ? isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'
                    : isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className={`w-4 h-4 ${analysis.sentiment.escalation.recommended ? 'text-red-500' : isDark ? 'text-slate-400' : 'text-muted-foreground'}`} />
                    <h3 className={`text-xs font-medium ${isDark ? 'text-slate-200' : ''}`}>Escalation</h3>
                    {analysis.sentiment.escalation.recommended && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isDark ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'}`}>
                        Recommended
                      </span>
                    )}
                  </div>
                  {analysis.sentiment.escalation.factors && analysis.sentiment.escalation.factors.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {analysis.sentiment.escalation.factors.map((factor, i) => (
                        <span key={i} className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-muted'}`}>
                          {factor}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Re-analyze button */}
              <Button variant="outline" size="sm" className="w-full" onClick={handleAnalyze}>
                <RefreshCw className="w-3 h-3 mr-2" />
                Re-analyze
              </Button>
            </>
          )}
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="flex-1 overflow-y-auto space-y-3">
          <AlertsTab
            analysis={analysis}
            contactIsVip={contact?.isVip}
            contactName={contact?.name}
            contactJobTitle={contact?.jobTitle}
            isDark={isDark}
            salesOpportunityData={analysis?.salesOpportunity}
            onCreateSalesTicket={handleCreateSalesTicket}
          />
        </TabsContent>

        {/* Chat Tab */}
        <TabsContent value="chat" className="flex-1 overflow-y-auto flex flex-col space-y-3">
          {/* Chat Messages */}
          <div className={`rounded-lg border p-3 flex-1 overflow-y-auto ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
            {chatMessages.length === 0 ? (
              <div className="text-center py-4">
                <Bot className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-primary/50' : 'text-primary/30'}`} />
                <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                  Ask me anything about this ticket!
                </p>
                <SuggestedQuestions />
              </div>
            ) : (
              <div className="space-y-3">
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[90%] text-sm p-3 rounded-lg ${
                      msg.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : isDark ? 'bg-slate-700 text-slate-200' : 'bg-muted'
                    }`}>
                      {msg.role === 'assistant' && (
                        <div className="flex items-center gap-1.5 mb-2 pb-2 border-b border-primary/20">
                          <Bot className="h-4 w-4 text-primary" />
                          <span className="text-primary font-bold text-xs">AI Assistant</span>
                        </div>
                      )}
                      {msg.role === 'user' && msg.images && msg.images.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-1.5">
                          {msg.images.map((img, imgIdx) => (
                            <img
                              key={imgIdx}
                              src={attachmentToDataUrl(img)}
                              alt={`Attachment ${imgIdx + 1}`}
                              className="max-h-32 max-w-[140px] rounded border border-white/30 object-contain"
                            />
                          ))}
                        </div>
                      )}
                      {msg.content && (
                        <div className={`whitespace-pre-wrap leading-relaxed ${msg.role === 'assistant' ? 'text-sm' : ''}`}>{msg.content}</div>
                      )}
                    </div>
                  </div>
                ))}
                {sendingChat && (
                  <div className="flex justify-start">
                    <div className={`rounded-lg px-3 py-2 shadow-sm ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border'}`}>
                      <div className="flex items-center gap-2 text-xs">
                        <Loader2 className="h-3 w-3 animate-spin text-primary" />
                        <span className={`transition-opacity duration-300 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>{THINKING_MESSAGES[thinkingMessageIndex]}</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            )}
          </div>

          {/* Suggested questions after assistant response */}
          {chatMessages.length > 0 && !sendingChat && chatMessages[chatMessages.length - 1]?.role === 'assistant' && (
            <div className={`p-2 rounded-lg border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
              <p className={`text-[10px] font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                Quick follow-ups:
              </p>
              <SuggestedQuestions />
            </div>
          )}

          {/* Chat Input */}
          <div className="flex-shrink-0 space-y-1.5">
            {/* Attached image thumbnails */}
            {chatImages.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {chatImages.map((img, idx) => (
                  <div key={idx} className="relative">
                    <img
                      src={attachmentToDataUrl(img)}
                      alt={`Attachment ${idx + 1}`}
                      className={`h-12 w-12 object-cover rounded border ${isDark ? 'border-slate-600' : 'border-gray-300'}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeChatImage(idx)}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-gray-700 text-white flex items-center justify-center shadow hover:bg-gray-900"
                      title="Remove image"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <Textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={handleChatKeyDown}
                onPaste={handleChatPaste}
                placeholder="Ask about this ticket... (Shift+Enter for new line, paste a screenshot)"
                disabled={sendingChat}
                rows={1}
                className={`flex-1 text-sm min-h-[2.5rem] max-h-32 resize-none ${isDark ? 'bg-slate-800 border-slate-600 text-white placeholder:text-slate-400' : 'bg-background'}`}
              />
              <input
                ref={chatFileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                multiple
                onChange={handleChatFileChange}
                className="hidden"
              />
              <Button
                type="button"
                size="icon"
                variant="outline"
                onClick={() => chatFileInputRef.current?.click()}
                disabled={sendingChat || processingImage || chatImages.length >= MAX_IMAGES}
                className="h-10 w-10 flex-shrink-0"
                title={chatImages.length >= MAX_IMAGES ? `Up to ${MAX_IMAGES} images` : 'Attach image'}
              >
                {processingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
              </Button>
              <Button
                size="icon"
                onClick={handleSendChat}
                disabled={sendingChat || (!chatInput.trim() && chatImages.length === 0)}
                className="h-10 w-10 flex-shrink-0"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Actions Tab */}
        <TabsContent value="actions" className="flex-1 overflow-y-auto space-y-3">
          {/* Success/Error Message */}
          {actionSuccess && (
            <div className={`p-2 rounded text-sm max-w-md ${
              actionSuccess.startsWith('Error')
                ? isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'
                : isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'
            }`}>
              {actionSuccess}
            </div>
          )}

          {/* Two column layout for wider screens */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Left Column - Notes & Response */}
            <div className="space-y-3">
              {/* AI Response Generator */}
              <div className={`rounded-lg border p-3 space-y-2 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
                <h3 className={`text-xs font-medium flex items-center gap-2 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                  <Wand2 className="w-3 h-3" />
                  AI Response Generator
                </h3>

                {!generatedResponse ? (
                  <>
                    {/* Tone Buttons */}
                    <div className="flex gap-1 max-w-xs">
                      {(['professional', 'friendly', 'technical'] as const).map((tone) => (
                        <button
                          key={tone}
                          onClick={() => setResponseTone(tone)}
                          disabled={generatingResponse}
                          className={`flex-1 px-2 py-1.5 text-[10px] font-medium rounded transition-colors ${
                            responseTone === tone
                              ? 'bg-primary text-primary-foreground'
                              : isDark
                              ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {tone.charAt(0).toUpperCase() + tone.slice(1)}
                        </button>
                      ))}
                    </div>
                    <Button size="sm" className="max-w-xs" onClick={handleGenerateResponse} disabled={generatingResponse}>
                      {generatingResponse ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wand2 className="w-4 h-4 mr-1" />}
                      Generate Response
                    </Button>
                  </>
                ) : (
                  <>
                    <div className={`p-2 rounded text-sm max-h-32 overflow-y-auto whitespace-pre-wrap ${isDark ? 'bg-slate-700 text-slate-200' : 'bg-muted'}`}>
                      {generatedResponse}
                    </div>
                    <div className="flex gap-1 max-w-xs">
                      <Button size="sm" variant="outline" className="flex-1" onClick={handleCopyResponse}>
                        <Copy className="w-3 h-3 mr-1" />
                        Copy
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1" onClick={handleUseAsNote}>
                        <FileText className="w-3 h-3 mr-1" />
                        Use as Note
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setGeneratedResponse('')}>
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {/* Add Note with Internal/Customer Toggle */}
              <div className={`rounded-lg border p-3 space-y-2 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
                <div className="flex items-center justify-between">
                  <h3 className={`text-xs font-medium flex items-center gap-2 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                    <PenLine className="w-3 h-3" />
                    Add Note
                  </h3>
                  {/* Internal/Customer Toggle */}
                  <div className="flex rounded-md overflow-hidden border">
                    <button
                      onClick={() => setNoteType('internal')}
                      className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                        noteType === 'internal'
                          ? 'bg-primary text-primary-foreground'
                          : isDark
                          ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      Internal
                    </button>
                    <button
                      onClick={() => setNoteType('customer')}
                      className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                        noteType === 'customer'
                          ? 'bg-blue-600 text-white'
                          : isDark
                          ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      Customer
                    </button>
                  </div>
                </div>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder={noteType === 'internal' ? 'Internal note (not visible to customer)...' : 'Customer-visible note...'}
                  className={`w-full px-3 py-2 text-sm border rounded-md resize-y min-h-[120px] ${
                    isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-background'
                  } ${noteType === 'customer' ? (isDark ? 'border-blue-600' : 'border-blue-300') : ''}`}
                  rows={6}
                  disabled={addingNote}
                />
                <Button size="sm" className="max-w-xs" onClick={handleAddNote} disabled={addingNote || !noteText.trim()}>
                  {addingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                  Send {noteType === 'customer' ? 'to Customer' : 'Internal Note'}
                </Button>
              </div>
            </div>

            {/* Right Column - Time & Actions */}
            <div className="space-y-3">
              {/* Quick Time Entry */}
              <div className={`rounded-lg border p-3 space-y-2 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
                <h3 className={`text-xs font-medium flex items-center gap-2 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                  <Clock className="w-3 h-3" />
                  Log Time
                </h3>
                {/* Quick Time Buttons */}
                <div className="flex gap-1 max-w-xs">
                  {[15, 30, 45, 60].map((mins) => (
                    <button
                      key={mins}
                      onClick={() => setTimeMinutesTotal(mins)}
                      disabled={addingTime}
                      className={`flex-1 px-2 py-1.5 text-[10px] font-medium rounded transition-colors ${
                        timeMinutesTotal === mins
                          ? 'bg-primary text-primary-foreground'
                          : isDark
                          ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {mins < 60 ? `${mins}m` : '1h'}
                    </button>
                  ))}
                  <input
                    type="number"
                    min="1"
                    max="480"
                    value={timeMinutesTotal}
                    onChange={(e) => setTimeMinutesTotal(Math.max(1, Number(e.target.value)))}
                    className={`w-14 px-2 py-1 text-[10px] text-center border rounded-md ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-background'}`}
                    disabled={addingTime}
                    title="Custom minutes"
                  />
                </div>
                {/* AI Suggested Time */}
                {analysis && (
                  <div className={`flex items-center gap-2 text-[10px] ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                    <Sparkles className="w-3 h-3 text-primary" />
                    <span>Suggested: {analysis.sentiment?.metrics?.acumen?.score >= 7 ? '15-30m' : analysis.sentiment?.metrics?.acumen?.score >= 4 ? '30-60m' : '60m+'}</span>
                  </div>
                )}
                <input
                  type="text"
                  value={timeNote}
                  onChange={(e) => setTimeNote(e.target.value)}
                  placeholder="Work description (optional)"
                  className={`w-full max-w-xs px-3 py-1.5 text-xs border rounded-md ${isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-background'}`}
                  disabled={addingTime}
                />
                <div className="flex items-center justify-between max-w-xs">
                  <label className={`flex items-center gap-1.5 text-[10px] ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                    <input
                      type="checkbox"
                      checked={timeBillable}
                      onChange={(e) => setTimeBillable(e.target.checked)}
                      disabled={addingTime}
                      className="rounded"
                    />
                    Billable
                  </label>
                  <Button size="sm" onClick={handleAddTime} disabled={addingTime || timeMinutesTotal === 0}>
                    {addingTime ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Timer className="w-3 h-3 mr-1" />
                        Log {timeMinutesTotal >= 60 ? `${Math.floor(timeMinutesTotal / 60)}h ${timeMinutesTotal % 60}m` : `${timeMinutesTotal}m`}
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Quick Actions */}
              <div className={`rounded-lg border p-3 space-y-2 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-card'}`}>
                <h3 className={`text-xs font-medium flex items-center gap-2 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                  <Play className="w-3 h-3" />
                  Quick Actions
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {/* Status Change Button */}
                  <div className="relative">
                    <button
                      onClick={handleLoadStatuses}
                      disabled={updatingStatus}
                      className={`w-full px-3 py-2 text-xs font-medium rounded-md border transition-colors ${
                        isDark
                          ? 'bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600'
                          : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {updatingStatus ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : 'Change Status'}
                    </button>
                    {showStatusDropdown && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowStatusDropdown(false)} />
                        <div className={`absolute bottom-full left-0 mb-1 border rounded-md shadow-lg z-20 min-w-full max-h-40 overflow-y-auto ${isDark ? 'bg-slate-800 border-slate-600' : 'bg-card'}`}>
                          {statuses.map(s => (
                            <button
                              key={s.id}
                              onClick={() => handleUpdateStatus(s.id)}
                              className={`w-full px-3 py-1.5 text-xs text-left transition-colors ${isDark ? 'hover:bg-slate-700 text-slate-200' : 'hover:bg-muted'}`}
                            >
                              {s.name}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  {/* Resolve Button */}
                  <button
                    onClick={() => {
                      const resolvedStatus = statuses.find(s => s.name.toLowerCase().includes('resolve'))
                      if (resolvedStatus) handleUpdateStatus(resolvedStatus.id)
                      else handleLoadStatuses()
                    }}
                    disabled={updatingStatus}
                    className={`px-3 py-2 text-xs font-medium rounded-md border transition-colors ${
                      isDark
                        ? 'bg-emerald-900/30 border-emerald-700 text-emerald-300 hover:bg-emerald-900/50'
                        : 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                    }`}
                  >
                    <CheckCircle className="w-3 h-3 inline mr-1" />
                    Resolve
                  </button>
                  {/* Escalate Button */}
                  <button
                    onClick={handleEscalate}
                    className={`px-3 py-2 text-xs font-medium rounded-md border transition-colors ${
                      isDark
                        ? 'bg-red-900/30 border-red-700 text-red-300 hover:bg-red-900/50'
                        : 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                    }`}
                  >
                    <ArrowUpCircle className="w-3 h-3 inline mr-1" />
                    Escalate
                  </button>
                  {/* Documentation Button */}
                  <button
                    onClick={handleDocumentation}
                    disabled={generatingDocSummary}
                    className={`px-3 py-2 text-xs font-medium rounded-md border transition-colors ${
                      isDark
                        ? 'bg-blue-900/30 border-blue-700 text-blue-300 hover:bg-blue-900/50'
                        : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                    }`}
                  >
                    <BookOpen className="w-3 h-3 inline mr-1" />
                    Documentation
                  </button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Recording Tab */}
        <TabsContent value="recording" className="flex-1 overflow-y-auto">
          <RecordingPanel
            ticketId={context?.ticket?.id ?? null}
            ticketSummary={context?.ticket?.summary ?? null}
            isDark={isDark}
          />
        </TabsContent>
      </Tabs>

      {/* Documentation Modal */}
      {showDocModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className={`w-full max-w-lg mx-4 rounded-lg border shadow-xl ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
            <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-slate-700' : ''}`}>
              <h3 className={`text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                <BookOpen className="w-4 h-4 text-blue-500" />
                Request Documentation
              </h3>
              <button
                onClick={() => { setShowDocModal(false); setDocSummary(''); }}
                className={`p-1 rounded hover:bg-slate-200 ${isDark ? 'hover:bg-slate-700 text-slate-400' : ''}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {docResult ? (
                /* Success state — show created ticket + doc link */
                <div className="space-y-3">
                  <div className={`flex items-center gap-2 text-sm font-medium ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                    <CheckCircle className="w-4 h-4" />
                    Documentation ticket #{docResult.ticketId} created
                  </div>
                  {docResult.docUrl && (
                    <a
                      href={docResult.docUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md border ${
                        isDark ? 'border-slate-600 bg-slate-700 text-blue-400 hover:bg-slate-600' : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                      }`}
                    >
                      <ExternalLink className="w-4 h-4" />
                      {docResult.docTitle || 'View Document'}
                    </a>
                  )}
                  {!docResult.docUrl && (
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-muted-foreground'}`}>
                      No doc platform configured — AI draft embedded in ticket description.
                    </p>
                  )}
                  {docResult.todoSections && docResult.todoSections.length > 0 && (
                    <div>
                      <p className={`text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>Sections to complete:</p>
                      <ul className={`text-xs space-y-0.5 ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                        {docResult.todoSections.map((todo, i) => (
                          <li key={i} className="flex items-start gap-1">
                            <span className="mt-0.5">•</span>
                            <span>{todo}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                /* Input state — show AI summary + textarea */
                <>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>
                    AI has analyzed this ticket{chatMessages.length > 0 ? ' and your Ask Bot questions' : ''} to suggest what documentation is needed:
                  </p>
                  {generatingDocSummary ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
                      <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-muted-foreground'}`}>Analyzing documentation needs...</span>
                    </div>
                  ) : (
                    <>
                      <textarea
                        value={docSummary}
                        onChange={(e) => setDocSummary(e.target.value)}
                        className={`w-full px-3 py-2 text-sm border rounded-md resize-y min-h-[150px] ${
                          isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-background'
                        }`}
                        placeholder="Describe what documentation is needed..."
                      />
                      <p className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-muted-foreground'}`}>
                        Edit the summary above if needed, then submit to create a documentation request.
                      </p>
                    </>
                  )}
                </>
              )}
            </div>
            <div className={`flex justify-end gap-2 px-4 py-3 border-t ${isDark ? 'border-slate-700' : ''}`}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setShowDocModal(false); setDocSummary(''); setDocResult(null); }}
                disabled={creatingDocTicket}
              >
                {docResult ? 'Close' : 'Cancel'}
              </Button>
              {!docResult && (
                <Button
                  size="sm"
                  onClick={handleCreateDocTicket}
                  disabled={generatingDocSummary || creatingDocTicket || !docSummary.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {creatingDocTicket ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <FileText className="w-4 h-4 mr-1" />
                  )}
                  Create Request
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 pt-3 border-t">
        <div className="flex items-center justify-center">
          <a href="https://xop.ai" target="_blank" rel="noopener noreferrer">
            <img src="/xop-logo.png" alt="xop.ai" className="h-12 w-auto" />
          </a>
        </div>
      </div>
    </div>
  )
}
